import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import Button from './ui/Button';
import Badge from './ui/Badge';
import { TrendingUp, Calendar, Banknote, Filter, ChevronDown, BarChart3, PieChart, X } from 'lucide-react';
import SelectedMetricsCards from './SelectedMetricsCards';
import './HistoricalTrend.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const HistoricalTrend = ({ 
  selectedBusinessSegment, 
  selectedBank, 
  selectedPeriodType, 
  selectedCurrentPeriod, 
  selectedCategory, 
  selectedFinancialItems,
  onUnselectItem
}) => {
  const { country } = useCountry();
  
  // Chart data
  const [periodData, setPeriodData] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState('actual'); // actual, qoq, yoy

  // Memoize fetchPeriods to prevent unnecessary re-creation
  const fetchPeriods = useCallback(async () => {
    try {
    setLoading(true);
    setError(null);

      const response = await axios.get(`${config.apiUrl}/period-data`, {
        params: {
          business_segment: selectedBusinessSegment,
          bank: selectedBank,
          period_type: selectedPeriodType,
          current_period: selectedCurrentPeriod,
          category: selectedCategory,
          financial_items: selectedFinancialItems.join(','),
          view_type: viewType
        },
        headers: getDefaultHeaders(country)
      });
      
      setPeriodData(response.data?.period_data || []);
      setPeriods(response.data?.periods || []);
    } catch (err) {
      console.error('Error fetching periods:', err);
      setError('Failed to load period data');
      setPeriodData([]);
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessSegment, selectedBank, selectedPeriodType, selectedCurrentPeriod, selectedCategory, selectedFinancialItems, viewType, country]);

  // Use ref to track previous selections to avoid unnecessary calls
  const prevSelectionsRef = useRef('');
  
  // Fetch data when selections change
  useEffect(() => {
    const selectionsKey = `${selectedBusinessSegment}-${selectedBank}-${selectedPeriodType}-${selectedCurrentPeriod}-${selectedFinancialItems.join(',')}-${viewType}`;
    
    if (selectedBusinessSegment && selectedBank && selectedPeriodType && 
        selectedCurrentPeriod && selectedFinancialItems.length > 0) {
      // Fetch if any selection actually changed (including viewType)
      if (prevSelectionsRef.current !== selectionsKey) {
        prevSelectionsRef.current = selectionsKey;
        fetchPeriods();
      }
    } else {
      prevSelectionsRef.current = '';
      setPeriodData([]);
      setPeriods([]);
    }
  }, [selectedBusinessSegment, selectedBank, selectedPeriodType, 
      selectedCurrentPeriod, selectedFinancialItems.join(','), viewType, fetchPeriods]);

  const handleUnselectItem = (itemId) => {
    if (onUnselectItem) {
      onUnselectItem(itemId);
    }
  };

  const convertPeriodToQuarter = (period) => {
    if (!period) return period;
    
    // Extract year and quarter from period string
    const year = period.split('_')[1];
    const quarterCode = period.split('_')[0];
    
    // Map quarter codes to Q1, Q2, Q3, Q4
    const quarterMap = {
      'jfm': 'Q1',
      'amj': 'Q2', 
      'jas': 'Q3',
      'ond': 'Q4'
    };
    
    const quarter = quarterMap[quarterCode] || quarterCode;
    return `${quarter} ${year}`;
  };

  const prepareChartData = () => {
    if (!periods || periods.length === 0 || !periodData || periodData.length === 0) {
      return [];
    }

    const chartData = periods.map(period => {
      const dataPoint = { period: convertPeriodToQuarter(period) };
      periodData.forEach(item => {
        dataPoint[item.Item] = item.periods[period] || 0;
      });
      return dataPoint;
    });

    return chartData;
  };

  const prepareSampleChartData = () => {
    return [
      { period: 'Q1 2024', Revenue: 3500000, 'Interest Income': 2900000, 'Net Income': 2000000 },
      { period: 'Q2 2024', Revenue: 3900000, 'Interest Income': 2900000, 'Net Income': 2000000 },
      { period: 'Q3 2024', Revenue: 4000000, 'Interest Income': 3200000, 'Net Income': 1400000 },
      { period: 'Q4 2024', Revenue: 3800000, 'Interest Income': 0, 'Net Income': 2500000 },
      { period: 'Q1 2025', Revenue: 4200000, 'Interest Income': 3400000, 'Net Income': 2800000 }
    ];
  };

  if (!selectedBusinessSegment) {
    return (
      <Card className="historical-trend-no-segment">
        <CardHeader>
          <CardTitle>Select Business Segment</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm mt-1">Choose a business segment above to view historical trends</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
         {/* Selected Metrics Cards - Show when financial items are selected */}
         {selectedFinancialItems.length > 0 && (
           <div className="selected-metrics-section">
             <SelectedMetricsCards
               selectedBusinessSegment={selectedBusinessSegment || ''}
               selectedFinancialItems={selectedFinancialItems}
               selectedBank={selectedBank}
               selectedCategory={selectedCategory}
               selectedPeriod={selectedCurrentPeriod}
               title="Historical Trend Metrics"
               description="Financial metrics for historical trend analysis"
               onUnselectItem={handleUnselectItem}
             />
           </div>
         )}

         {/* Chart Section */}
         <div className="chart-section">
         <div className="chart-header">
           <div className="chart-title">
             <BarChart3 className="w-4 h-4" />
             <span>Historical Trend Chart</span>
           </div>
           
           {/* View Type Toggle */}
           <div className="view-type-toggle">
             <button
               className={`view-type-button ${viewType === 'actual' ? 'active' : ''}`}
               onClick={() => setViewType('actual')}
             >
               Actual
             </button>
             <button
               className={`view-type-button ${viewType === 'qoq' ? 'active' : ''}`}
               onClick={() => setViewType('qoq')}
             >
               QoQ
             </button>
             <button
               className={`view-type-button ${viewType === 'yoy' ? 'active' : ''}`}
               onClick={() => setViewType('yoy')}
             >
               YoY
             </button>
           </div>
         </div>
         
         {/* Chart Controls */}
         <div className="chart-controls">
           <div className="chart-info">
             {periodData.length > 0 && periods.length > 0 ? (
               <span>Showing {periodData.length} metrics across {periods.length} periods ({viewType.toUpperCase()} view)</span>
             ) : (
               <span>Select filters above to load chart data</span>
             )}
           </div>
           
           {selectedFinancialItems.length > 0 && (
             <span className="chart-badge">
               {selectedFinancialItems.length} item(s) selected
             </span>
           )}
         </div>
            
        {loading ? (
          <div className="chart-container">
            <div className="chart-loading">
              <div className="loading-spinner"></div>
              <span>Loading chart data...</span>
            </div>
          </div>
        ) : error ? (
          <div className="chart-container">
            <div className="chart-error">
              <div className="chart-error-icon">
                <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2zm0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="chart-error-title">{error}</p>
              <p className="chart-error-description">Please check your filter selections</p>
            </div>
          </div>
        ) : periodData.length > 0 && periods.length > 0 ? (
          <div className="chart-container">
                {(() => {
                  const chartData = prepareChartData();
                  
                  if (chartData.length === 0) {
                    return (
                      <div className="no-data-container">
                        <div className="no-data-content">
                          <div className="no-data-icon">
                            <svg className="no-data-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2zm0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <p className="no-data-title">Chart data preparation failed</p>
                          <p className="no-data-description">Chart data is empty after processing</p>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="period" 
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                          tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                        />
                        <YAxis 
                          domain={['dataMin - 10%', 'dataMax + 10%']} 
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                          tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                          tickFormatter={(value) => {
                            const absValue = Math.abs(value);
                            if (absValue >= 1000000000) {
                              const roundedValue = Math.round(value / 100000000) / 10;
                              return `${roundedValue}B`;
                            } else if (absValue >= 1000000) {
                              const roundedValue = Math.round(value / 100000) / 10;
                              return `${roundedValue}M`;
                            } else if (absValue >= 1000) {
                              const roundedValue = Math.round(value / 100) / 10;
                              return `${roundedValue}K`;
                            } else if (absValue >= 1) {
                              return `${Math.round(value)}`;
                            } else if (absValue >= 0.01) {
                              return value.toFixed(2);
                            } else {
                              return value.toFixed(4);
                            }
                          }}
                        />
                        <Tooltip 
                          formatter={(value, name) => [value?.toLocaleString(), name]} 
                          labelFormatter={(label) => `Period: ${label}`}
                          contentStyle={{
                            fontSize: '10px',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            border: '1px solid #d1d5db'
                          }}
                          labelStyle={{
                            fontSize: '10px',
                            color: '#374151'
                          }}
                        />
                        <Legend 
                          wrapperStyle={{
                            fontSize: '10px',
                            paddingTop: '8px'
                          }}
                        />
                        {periodData.map((item, index) => (
                    <Line
                            key={item.id}
                      type="monotone"
                      dataKey={item.Item}
                            stroke={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                      strokeWidth={2}
                            dot={{ r: 2 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
        ) : (
          <>
            {/* Sample Chart for Demonstration */}
            <div className="sample-chart-header">
              <h4 className="sample-chart-title">Sample Historical Trend Chart</h4>
              <p className="sample-chart-subtitle">Demo data for demonstration</p>
            </div>
            <div className="chart-container">
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={prepareSampleChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                  tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                />
                <YAxis 
                  domain={['dataMin - 10%', 'dataMax + 10%']} 
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                  tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                  tickFormatter={(value) => {
                    const absValue = Math.abs(value);
                    if (absValue >= 1000000000) {
                      const roundedValue = Math.round(value / 100000000) / 10;
                      return `${roundedValue}B`;
                    } else if (absValue >= 1000000) {
                      const roundedValue = Math.round(value / 100000) / 10;
                      return `${roundedValue}M`;
                    } else if (absValue >= 1000) {
                      const roundedValue = Math.round(value / 100) / 10;
                      return `${roundedValue}K`;
                    } else if (absValue >= 1) {
                      return `${Math.round(value)}`;
                    } else if (absValue >= 0.01) {
                      return value.toFixed(2);
                    } else {
                      return value.toFixed(4);
                    }
                  }}
                />
                <Tooltip 
                  formatter={(value, name) => [value?.toLocaleString(), name]} 
                  labelFormatter={(label) => `Period: ${label}`}
                  contentStyle={{
                    fontSize: '10px',
                    padding: '6px 8px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db'
                  }}
                  labelStyle={{
                    fontSize: '10px',
                    color: '#374151'
                  }}
                />
                <Legend 
                  wrapperStyle={{
                    fontSize: '10px',
                    paddingTop: '8px'
                  }}
                />
                <Line type="monotone" dataKey="Revenue" stroke="#8884d8" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="Interest Income" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="Net Income" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
            </div>
          </>
        )}
       </div>
    </>
   );
};

export default HistoricalTrend;